import qs from 'qs';
import * as React from 'react';
import { Link, withRouter, } from 'react-router-dom';

import {
  fileDocumentIcon,
  PROFICIENCY,
  MOBILE_WIDTH,
  preToPostImprovedSkillsTooltipText,
  preQuestionsCorrectTooltipText,
  preSkillsProficientTooltipText,
  postQuestionsCorrectTooltipText,
  postSkillsImprovedOrMaintainTooltipText
} from './shared';
import ReportHeader from './reportHeader';

import { requestGet } from '../../../../../../modules/request/index';
import { DataTable, DataTableChip, accountGreenIcon, pluralize, Spinner } from '../../../../../Shared/index';
import useScaledTableColumnWidths from '../../../../../Shared/hooks/useScaledTableColumnWidths';
import useStickyTableHeader from '../../../../../Shared/hooks/useStickyTableHeader';
import useWindowSize from '../../../../../Shared/hooks/useWindowSize';
import { alphabeticalName, } from '../../../../../../modules/sortingMethods'

interface Student {
  name: string;
  id: number;
  total_possible_questions_count: number;
  total_correct_questions_count: number;
  total_pre_correct_questions_count: number;
  total_pre_possible_questions_count: number;
  skill_groups: any[];
  total_correct_skill_groups_count: number;
  total_acquired_skill_groups_count: number;
  total_maintained_skill_group_proficiency_count: number;
  correct_skill_groups_text: string;
}

const NOT_AVAILABLE = 'Not available'

const diagnosticNotCompletedElement = (<span>Diagnostic not completed</span>)

const preTestDesktopHeaders = (isSortable, columnWidths) => ([
  {
    name: 'Name',
    attribute: 'name',
    width: columnWidths['400w-150min'],
    rowSectionClassName: 'name-section',
    sortAttribute: 'alphabeticalName',
    isSortable: true,
    renderCellsAsHeaders: true
  },
  {
    name: '',
    attribute: 'activeDiagnosticSkillsCorrectElement',
    sortAttribute: 'totalCorrectSkillsCount',
    width: columnWidths['400w-150min'],
    rowSectionClassName: 'score-section',
    headerClassName: 'score-header',
    primaryTitle: 'Pre:',
    secondaryTitle: 'Questions Correct',
    tooltipName: 'Pre: Questions Correct',
    tooltipDescription: preQuestionsCorrectTooltipText,
    noTooltip: true,
    isSortable
  },
  {
    name: '',
    attribute: 'preSkillsProficientElement',
    sortAttribute: 'totalPreCorrectSkillsCount',
    width: columnWidths['400w-150min'],
    primaryTitle: 'Pre:',
    secondaryTitle: 'Skills Proficient',
    tooltipName: 'Pre: Skills Proficient',
    tooltipDescription: preSkillsProficientTooltipText,
    noTooltip: true,
    isSortable
  }
])

const postTestDesktopHeaders = (isSortable, columnWidths) => ([
  {
    name: 'Name',
    attribute: 'name',
    width: columnWidths['160w-140min'],
    sortAttribute: 'alphabeticalName',
    rowSectionClassName: 'name-section',
    isSortable: true,
    renderCellsAsHeaders: true
  },
  {
    name: '',
    attribute: 'preToPostImprovedSkills',
    sortAttribute: 'totalAcquiredSkillGroupsCount',
    width: columnWidths['172w-150min'],
    primaryTitle: 'Pre to Post:',
    secondaryTitle: 'Improved Skills',
    tooltipName: 'Pre to Post: Improved Skills',
    tooltipDescription: preToPostImprovedSkillsTooltipText,
    noTooltip: true,
    isSortable
  },
  {
    name: '',
    attribute: 'preSkillsCorrectElement',
    sortAttribute: 'totalPreCorrectQuestionsCount',
    width: columnWidths['180w-180min'],
    primaryTitle: 'Pre:',
    secondaryTitle: 'Questions Correct',
    tooltipName: 'Pre: Questions Correct',
    tooltipDescription: preQuestionsCorrectTooltipText,
    rowSectionClassName: 'score-section',
    headerClassName: 'score-header',
    noTooltip: true,
    isSortable
  },
  {
    name: '',
    attribute: 'preSkillsProficientElement',
    sortAttribute: 'totalPreCorrectSkillsCount',
    width: columnWidths['172w-150min'],
    primaryTitle: 'Pre:',
    secondaryTitle: 'Skills Proficient',
    tooltipName: 'Pre: Skills Proficient',
    tooltipDescription: preSkillsProficientTooltipText,
    noTooltip: true,
    isSortable
  },
  {
    name: '',
    attribute: 'activeDiagnosticSkillsCorrectElement',
    sortAttribute: 'totalCorrectSkillsCount',
    width: columnWidths['180w-180min'],
    primaryTitle: 'Post:',
    secondaryTitle: 'Questions Correct',
    tooltipName: 'Post: Questions Correct',
    tooltipDescription: postQuestionsCorrectTooltipText,
    rowSectionClassName: 'score-section',
    headerClassName: 'score-header',
    noTooltip: true,
    isSortable
  },
  {
    name: 'Post: Skills Improved or Maintained',
    attribute: 'postSkillsImprovedOrMaintained',
    sortAttribute: 'totalAcquiredOrMaintainedSkillGroupsCount',
    width: columnWidths['224w-196min'],
    primaryTitle: 'Post:',
    secondaryTitle: 'Skills Improved or Maintained',
    tooltipName: 'Post: Skills Improved or Maintained',
    tooltipDescription: postSkillsImprovedOrMaintainTooltipText,
    noTooltip: true,
    isSortable
  }
])


const mobileHeaders = (isSortable, columnWidths) => ([
  {
    name: 'Name',
    attribute: 'name',
    width: columnWidths['300w-110min'],
    rowSectionClassName: 'name-section',
    headerClassName: 'name-header',
    sortAttribute: 'alphabeticalName',
    isSortable: true,
    renderCellsAsHeaders: true
  },
  {
    name: 'Questions correct',
    attribute: 'skillsCorrectElement',
    sortAttribute: 'totalCorrectSkillsCount',
    width: columnWidths['200w-120min'],
    rowSectionClassName: 'score-section',
    headerClassName: 'score-header',
    noTooltip: true,
    isSortable
  }
])

function calculateSkillsPercentage(correct, total) {
  return Math.round((correct / total) * 100)
}

export const StudentResponsesIndex = ({ passedStudents, match, mobileNavigation, location, isPostDiagnostic, }) => {
  const containerRef = React.useRef<HTMLElement>(null);
  const tableWrapperRef = React.useRef<HTMLDivElement>(null);
  const desktopTableRef = React.useRef<HTMLTableElement>(null);
  const mobileTableRef = React.useRef<HTMLTableElement>(null);
  const [loading, setLoading] = React.useState<boolean>(!passedStudents);
  const [students, setStudents] = React.useState<Student[]>(passedStudents || []);
  const { width: windowWidth } = useWindowSize();

  const { activityId, classroomId, } = match.params
  const unitId = qs.parse(location.search.replace('?', '')).unit
  const unitQueryString = unitId ? `&unit_id=${unitId}` : ''

  // Pre-test desktop column widths (3 columns)
  const preTestColumnWidths = useScaledTableColumnWidths({
    columnWidths: [
      { width: 400, minWidth: 150 },  // Name
      { width: 400, minWidth: 150 },  // Pre: Questions Correct
      { width: 400, minWidth: 150 }   // Pre: Skills Proficient
    ],
    containerRef,
    maxTableWidth: 1200,
    minTableWidth: 450,
    additionalWidthRestrictions: 144,
  });

  // Post-test desktop column widths (6 columns)
  const postTestColumnWidths = useScaledTableColumnWidths({
    columnWidths: [
      { width: 160, minWidth: 140 },  // Name
      { width: 172, minWidth: 150 },  // Pre to Post: Improved Skills
      { width: 180, minWidth: 180 },  // Pre: Questions Correct
      { width: 172, minWidth: 150 },  // Pre: Skills Proficient
      { width: 180, minWidth: 180 },  // Post: Questions Correct
      { width: 224, minWidth: 196 }   // Post: Skills Improved or Maintained
    ],
    containerRef,
    maxTableWidth: 1200,
    minTableWidth: 996,
    additionalWidthRestrictions: 144,
  });

  // Mobile column widths (2 columns)
  const mobileColumnWidths = useScaledTableColumnWidths({
    columnWidths: [
      { width: 300, minWidth: 110 },  // Name
      { width: 200, minWidth: 120 }    // Questions correct
    ],
    containerRef,
    maxTableWidth: 1200,
    minTableWidth: 200,
    additionalWidthRestrictions: 144,
  });

  // Sticky headers for desktop table
  useStickyTableHeader({
    wrapperRef: tableWrapperRef,
    tableRef: desktopTableRef,
    enabled: students.length > 0 && windowWidth > MOBILE_WIDTH,
    dependencies: [
      isPostDiagnostic ? Object.values(postTestColumnWidths).join('|') : Object.values(preTestColumnWidths).join('|'),
      windowWidth
    ],
  });

  // Sticky headers for mobile table
  useStickyTableHeader({
    wrapperRef: tableWrapperRef,
    tableRef: mobileTableRef,
    enabled: students.length > 0 && windowWidth <= MOBILE_WIDTH,
    dependencies: [
      Object.values(mobileColumnWidths).join('|'),
      windowWidth
    ],
  });

  React.useEffect(() => {
    getStudents()
  }, [])

  React.useEffect(() => {
    setLoading(true)
    getStudents()
  }, [activityId, classroomId, unitId])

  function getStudents() {
    const link = isPostDiagnostic ? `/teachers/progress_reports/diagnostic_growth_results_summary?activity_id=${activityId}&classroom_id=${classroomId}` : `/teachers/progress_reports/diagnostic_results_summary?activity_id=${activityId}&classroom_id=${classroomId}${unitQueryString}`
    requestGet(link,
      (data) => {
        setStudents(data.student_results);
        setLoading(false)
      }
    )
  }

  function getPreSkillsProficientCount({ total_correct_questions_count, total_correct_skill_groups_count, skill_groups }) {
    if (total_correct_questions_count === undefined) { return null }

    if (total_correct_skill_groups_count) { return total_correct_skill_groups_count }

    return skill_groups.filter(skillGroup => skillGroup.pre_test_proficiency === PROFICIENCY).length
  }

  function renderPreToPostImprovedSkillsElement({ total_correct_questions_count, total_acquired_skill_groups_count }) {
    if (total_correct_questions_count === undefined) { return diagnosticNotCompletedElement }

    if (total_acquired_skill_groups_count === 0) { return '0 Improved Skills' }

    if (!total_acquired_skill_groups_count) { return NOT_AVAILABLE }

    return total_acquired_skill_groups_count === 1 ? '+1 Improved Skill' : `+${total_acquired_skill_groups_count} Improved Skills`
  }

  function renderPreSkillsCorrectElement({ total_correct_questions_count, total_pre_correct_questions_count, total_pre_possible_questions_count, total_possible_questions_count }) {
    if (total_correct_questions_count === undefined) { return null }

    if (!total_pre_correct_questions_count) { return NOT_AVAILABLE }

    return (
      <div className="skills-correct-element">
        <p>{total_pre_correct_questions_count} of {total_pre_possible_questions_count} Questions</p>
        <p>({(calculateSkillsPercentage(total_pre_correct_questions_count, total_possible_questions_count))}%)</p>
      </div>
    )
  }

  function renderPreSkillsProficient({ total_correct_questions_count, total_pre_correct_questions_count, skill_groups, total_correct_skill_groups_count, correct_skill_groups_text }) {
    if (total_correct_questions_count === undefined) { return null }

    if (!total_correct_questions_count && total_correct_questions_count !== 0) { return NOT_AVAILABLE }

    if (total_pre_correct_questions_count) {
      const countOfPreSkillsProficient = skill_groups.filter(skillGroup => skillGroup.pre_test_proficiency === PROFICIENCY).length
      const countOfSkillsToPractice = skill_groups.length - countOfPreSkillsProficient
      return (
        <div className="skills-correct-element">
          <p>{countOfPreSkillsProficient} of {skill_groups.length} {pluralize(countOfPreSkillsProficient, "Skill", "Skills")}</p>
          {!!countOfSkillsToPractice && <p>({countOfSkillsToPractice} {pluralize({ countOfSkillsToPractice }, "Skill", "Skills")} to Practice)</p>}
        </div>
      )
    }

    const countOfSkillsToPractice = skill_groups.length - total_correct_skill_groups_count
    return (
      <div className="skills-correct-element">
        <p>{correct_skill_groups_text}</p>
        {!!countOfSkillsToPractice && <p>({countOfSkillsToPractice} {pluralize(countOfSkillsToPractice, "Skill", "Skills")} to Practice)</p>}
      </div>
    )
  }

  function renderActiveDiagnosticSkillsCorrectElement({ total_correct_questions_count, total_possible_questions_count }) {
    if (isPostDiagnostic && !total_possible_questions_count) { return null }

    if (total_correct_questions_count === undefined) { return diagnosticNotCompletedElement }

    if (!total_correct_questions_count && total_correct_questions_count !== 0) { return NOT_AVAILABLE }

    return (
      <div className="skills-correct-element">
        <p>{total_correct_questions_count} of {total_possible_questions_count} Questions</p>
        <p>({calculateSkillsPercentage(total_correct_questions_count, total_possible_questions_count)}%)</p>
      </div>
    )
  }

  function renderPostSkillsImprovedOrMaintained({ total_pre_possible_questions_count, correct_skill_groups_text, total_acquired_skill_groups_count, total_maintained_skill_group_proficiency_count }) {
    if (total_pre_possible_questions_count === undefined) { return null }

    if (!total_pre_possible_questions_count) { return NOT_AVAILABLE }

    const acquiredSkillsText = total_acquired_skill_groups_count === 1 ? '1 Improved Skill' : `${total_acquired_skill_groups_count} Improved Skills`
    const maintainedSkillsText = `${total_maintained_skill_group_proficiency_count} Maintained`
    return (
      <div className="skills-correct-element">
        <p>{correct_skill_groups_text}</p>
        <p>({acquiredSkillsText}, {maintainedSkillsText})</p>
      </div>
    )
  }

  function renderStudentChip({ total_correct_questions_count, id, name, isDesktopView }) {
    if (isDesktopView && total_correct_questions_count === undefined) {
      return <DataTableChip icon={accountGreenIcon} label={name} />
    }
    if (total_correct_questions_count === undefined) {
      return <React.Fragment><span>{name}</span><span className="name-section-subheader">Diagnostic not completed</span></React.Fragment>
    }
    return (
      <Link className="student-responses-link" to={responsesLink(id)}>
        <DataTableChip icon={accountGreenIcon} label={name} />
      </Link>
    )
  }

  const responsesLink = (studentId: number) => unitId ? `/diagnostics/${activityId}/classroom/${classroomId}/responses/${studentId}?unit=${unitId}` : `/diagnostics/${activityId}/classroom/${classroomId}/responses/${studentId}`

  if (loading) { return <Spinner /> }

  const worthSorting = students.filter(s => s.total_correct_questions_count).length

  const desktopRows = students.map(student => {
    const { name, total_possible_questions_count, total_correct_questions_count, total_pre_correct_questions_count, total_pre_possible_questions_count, skill_groups, total_correct_skill_groups_count, correct_skill_groups_text, total_acquired_skill_groups_count, total_maintained_skill_group_proficiency_count, id } = student
    const totalAcquiredOrMaintainedSkillGroupsCount = total_acquired_skill_groups_count + total_maintained_skill_group_proficiency_count

    return {
      id: id || name,
      name: renderStudentChip({ total_correct_questions_count, id, name, isDesktopView: true }),
      alphabeticalName: alphabeticalName(name),
      preToPostImprovedSkills: renderPreToPostImprovedSkillsElement({ total_correct_questions_count, total_acquired_skill_groups_count }),
      totalCorrectSkillsCount: total_correct_questions_count,
      totalPreCorrectQuestionsCount: total_pre_correct_questions_count,
      totalPreCorrectSkillsCount: getPreSkillsProficientCount({ total_correct_questions_count, total_correct_skill_groups_count, skill_groups }),
      totalAcquiredSkillGroupsCount: total_acquired_skill_groups_count,
      totalAcquiredOrMaintainedSkillGroupsCount: totalAcquiredOrMaintainedSkillGroupsCount > 0 ? totalAcquiredOrMaintainedSkillGroupsCount : 0,
      preSkillsProficientElement: renderPreSkillsProficient({ total_correct_questions_count, total_pre_correct_questions_count, skill_groups, total_correct_skill_groups_count, correct_skill_groups_text }),
      preSkillsCorrectElement: renderPreSkillsCorrectElement({ total_correct_questions_count, total_pre_correct_questions_count, total_pre_possible_questions_count, total_possible_questions_count }),
      activeDiagnosticSkillsCorrectElement: renderActiveDiagnosticSkillsCorrectElement({ total_correct_questions_count, total_possible_questions_count }),
      postSkillsImprovedOrMaintained: renderPostSkillsImprovedOrMaintained({ total_pre_possible_questions_count, correct_skill_groups_text, total_acquired_skill_groups_count, total_maintained_skill_group_proficiency_count })
    }
  })

  const mobileRows = students.map(student => {
    const { name, total_possible_questions_count, total_correct_questions_count, id, } = student
    return {
      id: id || name,
      name: renderStudentChip({ total_correct_questions_count, id, name, isDesktopView: false }),
      alphabeticalName: alphabeticalName(name),
      totalCorrectSkillsCount: total_correct_questions_count,
      skillsCorrectElement: total_correct_questions_count !== undefined ? <div className="skills-correct-element">{total_correct_questions_count} of {total_possible_questions_count} ({calculateSkillsPercentage(total_correct_questions_count, total_possible_questions_count)}%)</div> : null
    }
  })

  return (
    <main className="student-responses-index-container" ref={containerRef}>
      <ReportHeader
        guideLink={<a className="quill-button small outlined grey focus-on-light" href="https://support.quill.org/en/articles/5698167-how-do-i-read-the-student-responses-report" rel="noopener noreferrer" target="_blank">{fileDocumentIcon}<span>Guide</span></a>}
        pageName="Student Responses"
        subHeader="See student performance by question"
      />
      {mobileNavigation}
      <div className="data-table-container" ref={tableWrapperRef}>
        <DataTable
          className={`larger-table ${isPostDiagnostic ? 'post-test' : 'pre-test'}`}
          defaultSortAttribute={worthSorting && 'totalCorrectSkillsCount'}
          defaultSortDirection='ascending'
          headers={isPostDiagnostic ? postTestDesktopHeaders(worthSorting, postTestColumnWidths) : preTestDesktopHeaders(worthSorting, preTestColumnWidths)}
          rows={desktopRows}
          tableRef={desktopTableRef}
        />
        <DataTable
          className="smaller-table"
          defaultSortAttribute={worthSorting && 'totalCorrectSkillsCount'}
          defaultSortDirection='ascending'
          headers={mobileHeaders(worthSorting, mobileColumnWidths)}
          rows={mobileRows}
          tableRef={mobileTableRef}
        />
      </div>
    </main>
  )
}

export default withRouter(StudentResponsesIndex)
